/**
 * ProducerProducts – discreet "Gårdsbutik" section on the public producer page.
 * Shows max 6 public products with media_assets cover images.
 * Uses ShopGate for canonical gating. Farmly tone: calm, not catalog-like.
 */

import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Store, ArrowRight, ImageOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useProducerShopProducts } from '@/hooks/useProducerShopProducts';
import { useProductCoverImages } from '@/hooks/useProductCoverImages';
import { track } from '@/lib/analytics';
import { stripHtmlToText } from '@/lib/text';
import { evaluateShopGate, isVisibleInPreview } from '@/lib/shop/shopGate';
import { producerProductPath } from '@/lib/paths';

interface ProducerProductsProps {
  producerId: string;
  producerName: string;
  producerSlug?: string | null;
  /** Producer fields needed for gating */
  producerStatus?: string;
  producerBusinessStatus?: string;
  producerVerified?: boolean;
  producerShopEnabled?: boolean;
  /** Whether farm_shop entitlement is enabled */
  hasFarmShopEntitlement?: boolean;
  pickupInstructions?: string | null;
}

const MAX_ITEMS_SHOP = 3;
const MAX_ITEMS_CURATED = 2;
const MAX_ITEMS_FETCH = 6;

export function ProducerProducts({
  producerId,
  producerName,
  producerSlug,
  producerStatus = 'public',
  producerBusinessStatus = 'active',
  producerVerified = false,
  producerShopEnabled = false,
  hasFarmShopEntitlement = false,
  pickupInstructions,
}: ProducerProductsProps) {
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get('preview') === '1' || searchParams.get('preview') === 'true';

  const gate = useMemo(() => evaluateShopGate({
    status: producerStatus,
    producer_status: producerBusinessStatus,
    verified: producerVerified,
    shop_enabled: producerShopEnabled,
    hasFarmShopEntitlement,
  }), [producerStatus, producerBusinessStatus, producerVerified, producerShopEnabled, hasFarmShopEntitlement]);

  // In preview, show section even if needs_enable/needs_verification
  // Show on public pages when gate is allowed, OR when the only blocker is
  // needs_enable/needs_verification (producer has entitlement + products exist).
  // This matches isVisibleInPreview logic: the section renders if the producer
  // has the farm_shop entitlement and products; shop_enabled is a portal publish
  // toggle that should not silently hide existing public products.
  const shouldShow = gate.allowed || isVisibleInPreview(gate.reason_key);

  const { products: allProducts, loading, totalCount } = useProducerShopProducts(
    shouldShow ? producerId : undefined,
    { limit: MAX_ITEMS_FETCH, onlyActive: true }
  );

  const isCurated = !loading && allProducts.length > 0 && allProducts.length <= 2;
  const displayLimit = isCurated ? MAX_ITEMS_CURATED : MAX_ITEMS_SHOP;
  const products = allProducts.slice(0, displayLimit);

  const productIds = products.map(p => p.id);
  const { data: coverImages = {} } = useProductCoverImages(productIds, 640);

  const shopUrl = `/producer/${producerSlug || producerId}/shop`;
  const hasMore = totalCount > displayLimit;

  // Don't render if gate says no (and not preview-visible)
  if (!shouldShow) return null;

  // Don't render if no products and not loading
  if (!loading && products.length === 0) return null;

  const sectionTitle = isCurated ? 'Finns att ta med hem' : 'Från gårdsbutiken';

  return (
    <section>
      <div className="mb-4">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
            <Store className="h-4 w-4 text-primary" />
          </div>
          <h2 className="font-display text-xl text-foreground">
            {sectionTitle}
          </h2>
        </div>
        {!isCurated && (
          <p className="text-sm text-muted-foreground mt-1.5 ml-11">
            Ett urval av produkter från gården.
          </p>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="space-y-2">
              <Skeleton className="aspect-square rounded-lg" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          ))}
        </div>
      ) : (
        <>
          <div className={isCurated
            ? "grid grid-cols-2 gap-3"
            : "grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4"
          }>
            {products.map(product => {
              const coverUrl = coverImages[product.id] || product.imageUrl;
              const descText = stripHtmlToText(product.description);

              return (
                <Card
                  key={product.id}
                  className="overflow-hidden border-border/50 hover:border-border transition-colors group cursor-pointer"
                >
                  <Link
                    to={producerProductPath(producerSlug || producerId, product.slug || product.id)}
                    className="block"
                    onClick={() => {
                      track('product_viewed', {
                        product_id: product.id,
                        producer_id: producerId,
                        surface: 'producer_detail',
                      });
                    }}
                  >
                    <div className={`${isCurated ? 'aspect-[4/3]' : 'aspect-square'} bg-muted relative overflow-hidden`}>
                      {coverUrl ? (
                        <img
                          src={coverUrl}
                          alt={product.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ImageOff className="h-8 w-8 text-muted-foreground/40" />
                        </div>
                      )}
                      {product.status === 'sold_out' && (
                        <div className="absolute inset-0 bg-background/80 flex items-center justify-center">
                          <span className="text-sm font-medium text-muted-foreground">Tillfälligt slut</span>
                        </div>
                      )}
                      {product.status === 'upcoming' && (
                        <div className="absolute inset-0 bg-background/60 flex items-center justify-center">
                          <span className="text-sm font-medium text-foreground/70">Kommer snart</span>
                        </div>
                      )}
                    </div>
                    <CardContent className="p-3">
                      <h3 className="font-medium text-foreground text-sm line-clamp-1 mb-0.5">
                        {product.title}
                      </h3>
                      {descText && (
                        <p className="text-xs text-muted-foreground line-clamp-1 mb-1">
                          {descText}
                        </p>
                      )}
                      {product.status === 'sold_out' ? (
                        <div>
                          <span className="text-xs font-medium text-muted-foreground">Tillfälligt slut</span>
                          <p className="text-xs text-muted-foreground/70">Finns igen snart</p>
                        </div>
                      ) : product.status === 'upcoming' ? (
                        <div>
                          <span className="text-xs font-medium text-foreground/70">Kommer snart</span>
                          <p className="text-xs text-muted-foreground/70">Till nästa utlämning</p>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground/70">
                          Finns i gårdsbutiken
                        </span>
                      )}

                      <div className="mt-1.5 inline-flex items-center gap-1 text-xs text-muted-foreground group-hover:text-foreground transition-colors">
                        Se produkt
                        <ArrowRight className="h-3 w-3" />
                      </div>
                    </CardContent>
                  </Link>
                </Card>
              );
            })}
          </div>

          {/* CTA – subtle link for curated, button for shop */}
          <div className="mt-4 text-center">
            {isCurated ? (
              <Link
                to={shopUrl}
                className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Se allt i gårdsbutiken
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ) : (
              <Button asChild variant="outline" size="sm" className="gap-1.5">
                <Link to={shopUrl}>
                  Se alla produkter
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </Button>
            )}
          </div>
        </>
      )}
    </section>
  );
}
